import type { Rng } from "./rng";

export type PlayerIndex = 0 | 1;

export const GAME_IDS = [
	// Classics
	"tictactoe",
	"connect4",
	"dotsboxes",
	"nim",
	"checkers",
	"fleet",
	"flipfour",
	"pitrow",
	"pegchase",
	"snakesladders",
	// Education
	"quiz",
	"times",
	"spelling",
	"wordladder",
	"geography",
	"sciencesort",
	"logicgrid",
	// Puzzle
	"memory",
	"fiveinrow",
	"codebreaker",
	"numberguess",
	"sliding",
	"minesweeper",
	"towerswap",
	"routeblocker",
	// Cards
	"war",
	"snap",
	"gofish",
	"oldmaid",
	"lastcard",
	"suitrun",
	"rummy",
	// Racing and reaction
	"reflex",
	"mathsprint",
	"typingladder",
	"snaildash",
	"turbotrack",
	// Music
	"notematch",
	// Art
	"dotsrace",
	"mosaic",
	// Party
	"rps",
	"wouldyourather",
	"charades",
] as const;
export type GameId = (typeof GAME_IDS)[number];

export const GAME_CATEGORIES = [
	"classics",
	"education",
	"puzzle",
	"cards",
	"racing",
	"music",
	"art",
	"party",
] as const;
export type GameCategory = (typeof GAME_CATEGORIES)[number];

export const CATEGORY_LABEL: Record<GameCategory, { title: string; emoji: string }> = {
	classics: { title: "Classics", emoji: "🎲" },
	education: { title: "Learn", emoji: "🧮" },
	puzzle: { title: "Puzzles", emoji: "🧩" },
	cards: { title: "Cards", emoji: "🃏" },
	racing: { title: "Fast", emoji: "⚡" },
	music: { title: "Music", emoji: "🎵" },
	art: { title: "Art", emoji: "🎨" },
	party: { title: "Silly", emoji: "🎉" },
};

export function isGameId(value: unknown): value is GameId {
	return typeof value === "string" && (GAME_IDS as readonly string[]).includes(value);
}

/** `winner: null` means a draw. */
export interface GameResult {
	winner: PlayerIndex | null;
}

export interface BaseState {
	kind: GameId;
	/** Whose turn it is, or `null` when both players act at once (the quiz). */
	turn: PlayerIndex | null;
	scores: [number, number];
	result: GameResult | null;
}

export type MoveOutcome<S> = { ok: true; state: S } | { ok: false; reason: string };

export function reject<S>(reason: string): MoveOutcome<S> {
	return { ok: false, reason };
}

/**
 * Everything a game needs to be playable. All methods are pure: the same inputs
 * always produce the same output, which is what lets the server be the single
 * source of truth and lets every rule be unit-tested without a network.
 */
export interface GameDefinition<S extends BaseState, M> {
	id: GameId;
	category: GameCategory;
	title: string;
	emoji: string;
	/** One line, written for a 10-year-old. */
	blurb: string;
	/** Rules, one short sentence per step. */
	howTo: readonly string[];

	create(rng: Rng, now: number): S;

	/** Validates untrusted input off the wire. Returns `null` if it is not a move. */
	parseMove(raw: unknown): M | null;

	apply(state: S, player: PlayerIndex, move: M, now: number): MoveOutcome<S>;

	/**
	 * Every move this player could legally make right now, or an empty list when
	 * it isn't their go. This is what the computer opponent picks from, and what
	 * the turn timer plays when someone wanders off mid-game.
	 */
	legalMoves(state: S, player: PlayerIndex): M[];

	/**
	 * Advances anything that depends on the clock (cards flipping back, a quiz
	 * timer running out). Returns `null` when there is nothing to do.
	 */
	tick(state: S, now: number): S | null;

	/** When `tick` next wants to be called, as an epoch ms, or `null`. */
	nextTickAt(state: S): number | null;

	/**
	 * Strips anything a player is not allowed to see yet — the faces of
	 * face-down cards, a quiz answer key. The server runs this per player before
	 * sending, so the secrets never reach the browser in the first place.
	 */
	redact?(state: S, viewer: PlayerIndex): S;
}

export function otherPlayer(player: PlayerIndex): PlayerIndex {
	return player === 0 ? 1 : 0;
}

/** Winner from the score table — used by the games that simply count points. */
export function resultFromScores(scores: [number, number]): GameResult {
	if (scores[0] === scores[1]) return { winner: null };
	return { winner: scores[0] > scores[1] ? 0 : 1 };
}
